import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import cx from 'classnames';
import AlgorithmicListCard from './AlgorithmicListCard';
import IconBadge from '../IconBadge';
import Link from '../Link';
import TranslationProvider, { getTranslation } from '../../providers/TranslationProvider';
import {
  getPageContext,
  parameterize,
  priceRangeToString,
} from '../../utils';

import './AlgorithmicLists.scss';
import { redirectToSanitizedUrl } from '../../utils/sanitizeTargetUrl';

const algorithmicListsPropTypes = {
  Analytics: PropTypes.shape({
    event: PropTypes.func.isRequired,
  }).isRequired,
  isDeltaWifi: PropTypes.bool,
  lists: PropTypes.shape({
    isLoading: PropTypes.bool,
    [PropTypes.string]: PropTypes.arrayOf(PropTypes.shape({
      info: PropTypes.shape({
        collectionSlug: PropTypes.string,
        image: PropTypes.string,
        name: PropTypes.string,
        translationKey: PropTypes.string,
      }),
      venues: PropTypes.arrayOf(PropTypes.shape({})),
    })),
  }),
  locationSlug: PropTypes.string.isRequired,
  searchSessionId: PropTypes.string,
  selectedDate: PropTypes.string.isRequired,
  selectedPartySize: PropTypes.number.isRequired,
  showAuthModal: PropTypes.func.isRequired,
  tagBooking: PropTypes.shape({
    event_name: PropTypes.string,
    utm: PropTypes.shape({}),
  }),
};

const getVenueImage = (images) => {
  if (!images) return null;

  const file = images?.file_names?.[0];

  if (!file) return null;

  return images?.urls?.[file]?.['1:1']?.['200'] || null;
};

const AlgorithmicLists = ({
  Analytics,
  isDeltaWifi,
  lists,
  locationSlug,
  selectedDate,
  selectedPartySize,
  showAuthModal,
  tagBooking,
  searchSessionId,
}) => {
  const seeMore = (name, hrefValue) => {
    const pageContext = getPageContext(window.location.pathname);

    Analytics.event('complete search', {
      context: pageContext,
      'filter type': 'list',
      value: name,
      searchSessionId,
    });

    if (hrefValue) {
      redirectToSanitizedUrl(hrefValue);
    }
  };

  const renderLeaderboards = () => {
    const { utm } = tagBooking;

    return lists[locationSlug].map((list) => {
      let hrefValue = `/cities/${locationSlug}/list/${list.info.collectionSlug}?date=${selectedDate}&seats=${selectedPartySize}`;

      if (isDeltaWifi) {
        const pathname = `/cities/${locationSlug}/list/${list.info.collectionSlug}`;
        const query = {
          date: selectedDate,
          seats: selectedPartySize,
          ...utm,
        };
        hrefValue = `${pathname}?${parameterize(query)}`;
      }

      return (
        <article
          className="ResyAlgorithmicLists__list-wrapper"
          key={list.info.name}
          aria-labelledby={`region-${list.info.collectionSlug}`}
        >
          <Link
            className="ResyButtonGroup__option"
            onClick={() => seeMore(list.info.name, hrefValue)}
            aria-label={getTranslation({
              translationKey: list.info.translationKey,
              fallback: `${list.info.name} - click to see more restaurants`,
            })}
          >
            <IconBadge
              aria-hidden
              bgToSvgRatio="1.3"
              height={33}
              className={`ResyBadge ResyBadge--${list.info.collectionSlug}`}
              name={list.info.collectionSlug}
              url={list.info.image}
              size="66"
            />
            <TranslationProvider translationKey={list.info.translationKey}>
              <h2
                className="h3 ResyAlgorithmicLists__name transform--capitalize"
                id={`region-${list.info.collectionSlug}`}
              >
                {list.info.name}
              </h2>
            </TranslationProvider>
          </Link>

          <div className="ResyAlgorithmicLists__list">
            {list.venues?.map((venue, index) => {
              let path = `cities/${locationSlug}/venues/${venue.url_slug}?date=${selectedDate}&seats=${selectedPartySize}`;

              if (isDeltaWifi) {
                const pathname = `cities/${locationSlug}/venues/${venue.url_slug}`;
                const query = {
                  date: selectedDate,
                  seats: selectedPartySize,
                  ...utm,
                };
                path = `${pathname}?${parameterize(query)}`;
              }

              return (
                <AlgorithmicListCard
                  className={cx({ last: index === list.venues.length - 1 })}
                  collectionName={list.info.name}
                  cuisine={venue?.type}
                  href={path}
                  image={getVenueImage(venue?.responsive_images)}
                  id={venue?.id?.resy}
                  isDeltaWifi={isDeltaWifi}
                  isLoading={lists?.isLoading}
                  key={venue?.name}
                  locality={venue?.location?.locality}
                  name={venue?.name}
                  neighborhood={venue?.location?.neighborhood}
                  price={priceRangeToString(venue?.price_range_id, venue?.currency_symbol)}
                  rating={venue?.rater?.[0]?.score}
                  ratings={venue?.rater?.[0]?.total}
                  showAuthModal={showAuthModal}
                  tagBooking={tagBooking}
                />
              );
            })}
          </div>

          <TranslationProvider translationKey="see_all">
            <Link
              aria-label={getTranslation({
                translationKey: 'see_more_collection',
                translationValues: { collection: list.info.name },
                fallback: `See more ${list.info.name}`,
              })}
              className="ResyAlgorithmicLists__see-all"
              onClick={() => seeMore(list.info.name, hrefValue)}
            >
              See All
            </Link>
          </TranslationProvider>
        </article>
      );
    });
  };

  const renderSkeleton = () => (
    <div className="AlgorithmicLists">
      <article className="container ResyHome">
        <section className="row">
          <div className="col">
            <div className="ResyAlgorithmicLists">
              <div className="ResyAlgorithmicLists__list-wrapper skeleton" />
              <div className="ResyAlgorithmicLists__list-wrapper skeleton" />
              <div className="ResyAlgorithmicLists__list-wrapper skeleton" />
            </div>
          </div>
        </section>
      </article>
    </div>
  );

  const renderAlgorithmicLists = () => (
    <div className="AlgorithmicLists">
      <article
        className="container ResyHome"
        role="main"
        aria-label={getTranslation({
          translationKey: 'algorithmic_lists_description',
          fallback: 'Algorithmic lists: climbing, top rated, and new on Resy',
        })}
      >
        <div className="ResyAlgorithmicLists">
          {renderLeaderboards()}
        </div>
      </article>
    </div>
  );

  const shouldRenderLists = () => {
    const locationLists = lists[locationSlug];
    const filteredLists = locationLists?.filter((list) => list.venues.length !== 0);
    return filteredLists?.length > 0 && locationLists?.every((locationList) => locationList?.venues?.length >= 5);
  };

  if (lists.isLoading) {
    return renderSkeleton();
  }

  if (shouldRenderLists()) {
    return renderAlgorithmicLists();
  }

  return null;
};

AlgorithmicLists.propTypes = algorithmicListsPropTypes;

AlgorithmicLists.defaultProps = {
  lists: {},
  isDeltaWifi: false,
  tagBooking: {},
  searchSessionId: '',
};

export const mapStateToProps = ({
  collections,
  venueSearch,
}) => ({
  lists: collections?.lists?.leaderboards,
  searchSessionId: venueSearch?.metadata?.searchSessionId,
});

AlgorithmicLists.displayName = 'AlgorithmicLists';
export {
  algorithmicListsPropTypes,
  AlgorithmicLists as ComponentUnderTest,
};
export default connect(mapStateToProps)(AlgorithmicLists);
